import { useState, useRef, useCallback } from 'react';
import { Settings, Zap, Upload, Plus } from 'lucide-react';
import { DocumentFile, DocumentSettings } from '@/types';
import DocumentUploadArea from './DocumentUploadArea';
import DocumentSettingsPanel from './DocumentSettingsPanel';
import DocumentListCompact from './DocumentListCompact';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface DocumentConverterPageProps {
  documents: DocumentFile[];
  settings: DocumentSettings;
  isProcessing: boolean;
  onFileSelect: (files: FileList) => void;
  onProcessDocument: (document: DocumentFile) => void;
  onProcessAll: () => void;
  onDownloadDocument: (document: DocumentFile) => void;
  onDownloadAll: () => void;
  onDownloadAllAsZip: () => void;
  onRemoveDocument: (id: string) => void;
  onClearAll: () => void;
  onUpdateSettings: (key: keyof DocumentSettings, value: any) => void;
  isDarkMode: boolean;
}

export default function DocumentConverterPage({
  documents,
  settings,
  isProcessing,
  onFileSelect,
  onProcessDocument,
  onProcessAll,
  onDownloadDocument,
  onDownloadAll,
  onDownloadAllAsZip,
  onRemoveDocument,
  onClearAll,
  onUpdateSettings,
  isDarkMode
}: DocumentConverterPageProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasDocuments = documents.length > 0;

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFileSelect(e.target.files);
      // Limpar o input para permitir seleção do mesmo arquivo novamente
      e.target.value = '';
    }
  };

  // Handlers para drag & drop na área Empty State
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileSelect(files);
    }
  }, [onFileSelect]);

  const handleEmptyStateClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full max-w-none space-y-3 sm:space-y-4">
      {/* Botão de Configurações */}
      <div className="flex justify-end">
        <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="flex items-center space-x-1.5 text-xs sm:text-sm"
            >
              <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Configurações</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#383D52' }}>
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Configurações de Conversão</span>
              </DialogTitle>
              <DialogDescription>
                Configure os parâmetros padrão para conversão de documentos
              </DialogDescription>
            </DialogHeader>
            <DocumentSettingsPanel 
              settings={settings}
              onUpdateSettings={onUpdateSettings}
              onClose={() => setIsSettingsOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Área de Upload - Mostrar apenas quando não há documentos */}
      {!hasDocuments && (
        <div className="w-full">
          <DocumentUploadArea onFileSelect={onFileSelect} />
        </div>
      )}

      {/* Input file oculto para adicionar mais arquivos */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.docm,.odt,.dot,.xps,.rtf,.dotx,.djvu,.sxw,.aw,.dotm,.oxps,.abw,.kwd,.dbk,.txt"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Quick Actions */}
      {hasDocuments && (
        <div className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#2A2E3F] border-[#4A4F64]' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-md ${
              isDarkMode ? 'bg-green-500/20' : 'bg-green-50'
            }`}>
              <Zap className="w-4 h-4 text-green-400" />
            </div>
            <div>
              <p className={`text-sm text-white`}>
                {documents.length} {documents.length === 1 ? 'documento' : 'documentos'}
              </p>
              <p className={`text-sm text-[#94A3B8]`}>
                Pronto para conversão
              </p>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex items-center gap-2">
            {/* Botão de Upload - sempre visível */}
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              size="sm"
              className="flex items-center space-x-1.5 text-xs sm:text-sm h-8"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Escolher documentos</span>
              <span className="sm:hidden">Adicionar</span>
            </Button>
            
            {/* Botões Converter e Limpar - ocultos no Desktop (lg:hidden) */}
            <Button
              onClick={onProcessAll}
              variant="default"
              size="sm"
              className="lg:hidden flex items-center space-x-1.5 text-xs sm:text-sm h-8"
            >
              <span>Converter Todas</span>
            </Button>
            
            <Button
              onClick={onClearAll}
              variant="destructive"
              size="sm"
              className="lg:hidden flex items-center space-x-1.5 text-xs sm:text-sm h-8"
            >
              <span>Limpar Tudo</span>
            </Button>
          </div>
        </div>
      )}

      {/* Lista de Documentos */}
      {hasDocuments && (
        <div className="w-full">
          <DocumentListCompact 
            documents={documents}
            onProcessDocument={onProcessDocument}
            onProcessAll={onProcessAll}
            onDownloadDocument={onDownloadDocument}
            onDownloadAll={onDownloadAll}
            onDownloadAllAsZip={onDownloadAllAsZip}
            onRemoveDocument={onRemoveDocument}
            onClearAll={onClearAll}
            isProcessing={isProcessing}
            settings={settings}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {/* Empty State com Drag & Drop */}
      {!hasDocuments && (
        <div 
          className={`text-center py-8 px-4 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-300 ${
            isDragActive
              ? isDarkMode ? 'border-blue-400 bg-blue-500/10' 
                : 'border-blue-500 bg-blue-50'
              : isDarkMode 
                ? 'border-[#4A4F64] bg-[#2A2E3F]/50 hover:border-blue-400/50 hover:bg-blue-500/5' 
                : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleEmptyStateClick}
        >
          <Upload className={`w-10 h-10 mx-auto mb-3 transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-blue-400' : 'text-blue-500'
              : isDarkMode ? 'text-gray-500 hover:text-blue-400' : 'text-gray-400 hover:text-blue-500'
          }`} />
          <h3 className={`text-lg font-medium mb-2 transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-blue-300' : 'text-blue-600'
              : isDarkMode ? 'text-white hover:text-blue-300' : 'text-gray-900 hover:text-blue-600'
          }`}>
            {isDragActive ? 'Solte seus documentos aqui' : 'Nenhum documento carregado'}
          </h3>
          <p className={`text-sm transition-colors duration-300 ${
            isDragActive
              ? isDarkMode ? 'text-blue-400' : 'text-blue-500'
              : isDarkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-600 hover:text-blue-500'
          }`}>
            {isDragActive ? 'Solte para adicionar à lista' : 'Arraste e solte seus documentos ou clique para selecionar'}
          </p>
        </div>
      )}
    </div>
  );
} 